import { useState } from 'react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { motion, AnimatePresence } from 'framer-motion'

import { useLayoutContext } from '../../../../../contexts/LayoutContext'
import { useProfileContext } from '../../../../../contexts/ProfileContext'
import renderComponent from './utils/renderComponent'
import style from './syle.module.css'

/* ---- Drag handle icon (6-dot grip) ---- */
const GripIcon = () => (
  <svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="3" cy="3"  r="1.8" fill="rgba(97,72,124,0.55)" />
    <circle cx="3" cy="9"  r="1.8" fill="rgba(97,72,124,0.55)" />
    <circle cx="3" cy="15" r="1.8" fill="rgba(97,72,124,0.55)" />
    <circle cx="11" cy="3"  r="1.8" fill="rgba(97,72,124,0.55)" />
    <circle cx="11" cy="9"  r="1.8" fill="rgba(97,72,124,0.55)" />
    <circle cx="11" cy="15" r="1.8" fill="rgba(97,72,124,0.55)" />
  </svg>
)

/* ---- Single sortable item ---- */
const SortableItem = ({ id, editMode, userProfile, toggleModal, openModal }) => {
  const { layout, setLayout, saveLayout } = useLayoutContext()

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const itemStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    position: 'relative',
  }

  const removeComponent = (componentId) => {
    const userId = userProfile.user.user_id
    const updated = layout.filter((item) => item.id !== componentId)
    setLayout(updated)
    saveLayout(updated, userId)
  }

  return (
    <div ref={setNodeRef} style={itemStyle}>
      {/* Drag handle — only shows in edit mode */}
      {editMode && (
        <div
          {...attributes}
          {...listeners}
          className={style.dragHandle}
          title="Drag to reorder"
        >
          <GripIcon />
        </div>
      )}

      {renderComponent({
        id,
        userProfile,
        editMode,
        toggleModal,
        openModal,
        removeComponent,
      })}
    </div>
  )
}

/* ---- Drag overlay ghost ---- */
const DragGhost = () => (
  <div
    style={{
      background: 'rgba(97,72,124,0.15)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      border: '2px dashed rgba(97,72,124,0.4)',
      borderRadius: 22,
      height: 64,
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'rgba(97,72,124,0.6)',
      fontFamily: 'Poppins, sans-serif',
      fontSize: 13,
      fontWeight: 600,
      gap: 8,
    }}
  >
    <GripIcon />
    Drop here
  </div>
)

/* ---- Main layout component ---- */
const ProfileLayout = ({ editMode, toggleModal, openModal }) => {
  const { layout, setLayout, saveLayout } = useLayoutContext()
  const { userProfile } = useProfileContext()
  const [activeId, setActiveId] = useState(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  if (!layout || layout.length === 0) return null

  const ids = layout.map((item) => item.id)

  const handleDragStart = ({ active }) => setActiveId(active.id)

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null)
    if (!over || active.id === over.id) return
    const oldIndex = layout.findIndex((i) => i.id === active.id)
    const newIndex = layout.findIndex((i) => i.id === over.id)
    const updated = arrayMove(layout, oldIndex, newIndex)
    setLayout(updated)
    saveLayout(updated, userProfile.user.user_id)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className={style.gridContainer}>
          <AnimatePresence>
            {layout.map((item) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              >
                <SortableItem
                  id={item.id}
                  editMode={editMode}
                  userProfile={userProfile}
                  toggleModal={toggleModal}
                  openModal={openModal}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </SortableContext>

      <DragOverlay dropAnimation={{ duration: 220, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }}>
        {activeId ? <DragGhost /> : null}
      </DragOverlay>
    </DndContext>
  )
}

export default ProfileLayout
